import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
  PayloadAction,
} from '@reduxjs/toolkit';

import { RootState } from '../..';
import { ICourse } from '../CoursesSlice';
import {
  getWaitingRoomMeetingDataByWaitingId,
  getWaitingRoomMeetingDataByWaitingIdInBackground,
  notifyUserJoinedToMeeting,
  notifyUserWaiting,
  startMeeting,
} from './api';
import { ISection } from '../SectionSlice';
import { IUser } from '../../UserSlice';
import { IMeetingRequest } from '../MeetingRequestsSlice';
import { getAuthUser } from '../../AuthSlice/AuthApi';

export const WAITING_ROOM_USER_TYPES = {
  STUDENT: 'STUDENT',
  TUTOR: 'TUTOR',
  ADMIN: 'ADMIN',
};

export type TWaitingRoomUserType = keyof typeof WAITING_ROOM_USER_TYPES;

export type TWaitingRoomMeetingData = {
  tutor: IUser;
  students: IUser[];
  courseData: ICourse;
  meetingData: ISection;
  studentHasLowCredits?: boolean;
  meetingRequest?: IMeetingRequest;
  waitingRoomUserType: TWaitingRoomUserType;
};

export interface IWaitingRoomConnection {
  _id: string;
  section: string;
  waitingId: string;
  userCount: number;
}

export interface TWaitingRoomSocketConnectionInitiateData {
  sectionId: string;
  waitingId: string;
}

export interface IInitialWaitingRoomState {
  status: 'idle' | 'loading' | 'rejected';
  authUserId?: string;
  isWaitingRoomSocketConnected: boolean;
  waitingRoomMeetingData?: TWaitingRoomMeetingData;
  errorMessage?: string;
  waitingRoomConnections: IWaitingRoomConnection[];
}

const initialState: IInitialWaitingRoomState = {
  status: 'idle',
  isWaitingRoomSocketConnected: false,
  authUserId: undefined,
  waitingRoomMeetingData: undefined,
  waitingRoomConnections: [],
};

const waitingRoomSocketRequestActions = {
  setWaitingRoomSocketConnection: (state, action: PayloadAction<boolean>) => {
    state.isWaitingRoomSocketConnected = action.payload;
  },
  initializeWaitingRoomSocketConnection: (
    state,
    action: PayloadAction<TWaitingRoomSocketConnectionInitiateData>
  ) => {
    // Request
  },
  registerToWaitingRoom: (
    state,
    action: PayloadAction<{ waitingId: string }>
  ) => {
    // Request
  },
  unRegisterFromWaitingRoom: (
    state,
    action: PayloadAction<{ waitingId: string }>
  ) => {
    console.log('unRegisterFromWaitingRoom', action.payload);
    // Request
  },
  disconnectWaitingRoomSocketConnection: (state) => {
    // Request
  },
};

const waitingRoomSocketResultActions = {
  onRegisteredToWaitingRoom: (
    state: IInitialWaitingRoomState,
    action: PayloadAction<IWaitingRoomConnection[]>
  ) => {
    const result = action.payload || [];

    state.waitingRoomConnections = [...result];
  },
  onUnRegisteredFromWaitingRoom: (
    state: IInitialWaitingRoomState,
    action: PayloadAction<IWaitingRoomConnection[]>
  ) => {
    const result = action.payload || [];

    state.waitingRoomConnections = [...result];
  },
};

export const WaitingRoomSlice = createSlice({
  initialState,
  name: 'waitingRoom',
  reducers: {
    ...waitingRoomSocketRequestActions,
    ...waitingRoomSocketResultActions,
    resetWaitingRoomMeetingData: (state) => {
      state.waitingRoomMeetingData = undefined;
    },
    resetWaitingRoomErrorMessage: (state) => {
      state.errorMessage = undefined;
    },
    resetWaitingRoomSocketData: (state) => {
      state.waitingRoomConnections = [];
      state.isWaitingRoomSocketConnected = false;
      state.waitingRoomMeetingData = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAuthUser.fulfilled, (state, action) => {
        const result = action.payload;

        if (result) {
          state.authUserId = result._id;
        }
      })
      .addCase(
        getWaitingRoomMeetingDataByWaitingId.fulfilled,
        (state, action) => {
          state.waitingRoomMeetingData = action.payload;
          state.errorMessage = undefined;
        }
      )
      .addCase(
        getWaitingRoomMeetingDataByWaitingIdInBackground.fulfilled,
        (state, action) => {
          state.waitingRoomMeetingData = action.payload;
          state.errorMessage = undefined;
        }
      )
      .addCase(startMeeting.fulfilled, (state, action) => {
        state.waitingRoomMeetingData = action.payload;
      })
      .addCase(notifyUserJoinedToMeeting.fulfilled, (state, action) => {
        state.waitingRoomMeetingData = action.payload;
      })
      .addCase(
        getWaitingRoomMeetingDataByWaitingId.rejected,
        (state, action) => {
          const errorData = JSON.parse(action.error.message || '');

          if (errorData) {
            state.errorMessage = errorData?.data?.message || undefined;
          }
        }
      )
      .addCase(
        getWaitingRoomMeetingDataByWaitingIdInBackground.rejected,
        (state, action) => {
          state.waitingRoomMeetingData = undefined;
          const errorData = JSON.parse(action.error.message || '');

          if (errorData) {
            state.errorMessage = errorData?.data?.message || undefined;
          }
        }
      );

    builder
      .addMatcher(
        isPending(
          getWaitingRoomMeetingDataByWaitingId,
          startMeeting,
          notifyUserJoinedToMeeting,
          notifyUserWaiting
        ),
        (state) => {
          state.status = 'loading';
        }
      )
      .addMatcher(
        isRejected(
          getWaitingRoomMeetingDataByWaitingId,
          startMeeting,
          notifyUserJoinedToMeeting,
          notifyUserWaiting
        ),
        (state) => {
          state.status = 'rejected';
        }
      )
      .addMatcher(
        isFulfilled(
          getWaitingRoomMeetingDataByWaitingId,
          startMeeting,
          notifyUserJoinedToMeeting,
          notifyUserWaiting
        ),
        (state) => {
          state.status = 'idle';
        }
      );
  },
});

export const getWaitingRoomState = (
  state: RootState
): RootState['waitingRoom'] => state.waitingRoom;

export const {
  resetWaitingRoomMeetingData,
  resetWaitingRoomErrorMessage,
  resetWaitingRoomSocketData,
  // Socket
  registerToWaitingRoom,
  unRegisterFromWaitingRoom,
  onRegisteredToWaitingRoom,
  onUnRegisteredFromWaitingRoom,
  initializeWaitingRoomSocketConnection,
  setWaitingRoomSocketConnection,
  disconnectWaitingRoomSocketConnection,
} = WaitingRoomSlice.actions;

export * from './api';
export default WaitingRoomSlice.reducer;
